import { useRef } from 'react';

export interface StableKey {
  get: () => string;
  clear: () => void;
}

/**
 * One idempotency key per paid request (rules/ai-star-billing.md): the same key is sent until the
 * request succeeds, so a retried tap after a lost response never charges twice. `clear` after a
 * success, or when the learner changes what the request asks for. The returned object is the same
 * on every render, so it is safe in effect dependencies.
 */
export function useStableKey(scope?: string): StableKey {
  const ref = useRef<{ value: string | null; scope?: string; api: StableKey } | null>(null);
  if (ref.current === null) {
    const state: { value: string | null; scope?: string; api: StableKey } = {
      value: readKey(scope),
      scope,
      api: {
        get: () => {
          state.value ??= crypto.randomUUID();
          storeKey(state.scope, state.value);
          return state.value;
        },
        clear: () => {
          state.value = null;
          storeKey(state.scope, null);
        },
      },
    };
    ref.current = state;
  }
  if (ref.current.scope !== scope) {
    ref.current.scope = scope;
    ref.current.value = readKey(scope);
  }
  return ref.current.api;
}

function readKey(scope?: string): string | null {
  if (!scope) return null;
  try {
    const key = sessionStorage.getItem(`airbotix.study.operation.${scope}`);
    return key && /^[0-9a-f-]{36}$/i.test(key) ? key : null;
  } catch {
    return null;
  }
}
function storeKey(scope: string | undefined, value: string | null) {
  if (!scope) return;
  try {
    const key = `airbotix.study.operation.${scope}`;
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    /* Same-mount retries still use the in-memory identity. */
  }
}
